/**
 * Unexcused absence - غياب بدون عذر (owner's requirement, 2026-11).
 *
 * A department manager (Manager role only) reports a direct report's absence
 * with no valid excuse or prior notice; HR confirms or dismisses it and the
 * record stays on the employee's HR file. The employee does not see it.
 *
 *  - ReportUnexcusedAbsence: the manager's form and their own reports (/leave/unexcused-absence).
 *  - UnexcusedAbsenceList: HR's list with confirm / dismiss - the pending queue
 *    on Leave Admin, and every report on the employee's file.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { CheckCircle, UserX, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const MIN_NOTE = 10;

const STATUS = {
  pending: { cls: 'bg-amber-100 text-amber-800', en: 'Awaiting HR review', ar: 'بانتظار مراجعة الموارد البشرية' },
  confirmed: { cls: 'bg-red-100 text-red-800', en: 'Confirmed unexcused', ar: 'غياب بدون عذر مؤكد' },
  dismissed: { cls: 'bg-slate-100 text-slate-700', en: 'Dismissed', ar: 'مستبعد' },
};

const day = (value) => {
  try { return format(new Date(value), 'dd MMM yyyy'); } catch { return value; }
};

// The server refuses a future date in Saudi time; the picker stops at the same day.
const todaySaudi = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Riyadh' }).format(new Date());

const errorText = (e, fallback) => {
  const d = e.response?.data?.detail;
  return (typeof d === 'string' ? d : d?.error) || (Array.isArray(d) ? d.map((x) => x.msg).join('; ') : fallback);
};

const StatusBadge = ({ status, isRTL }) => {
  const s = STATUS[status] || STATUS.pending;
  return <Badge className={s.cls}>{isRTL ? s.ar : s.en}</Badge>;
};

const nameOf = (x, isRTL, key = 'employee') => (isRTL && x[`${key}_name_ar`]) || x[`${key}_name`] || x[`${key}_name_ar`] || '-';

export const ReportUnexcusedAbsence = ({ isRTL }) => {
  const { token } = useAuth();
  const [members, setMembers] = useState([]);
  const [items, setItems] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [absenceDate, setAbsenceDate] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(() => {
    const config = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API_URL}/leave/unexcused-absences/team`, config)
      .then((r) => setMembers(r.data.members || [])).catch(() => setMembers([]));
    axios.get(`${API_URL}/leave/unexcused-absences/mine`, config)
      .then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true); setError(''); setSaved(false);
    try {
      await axios.post(`${API_URL}/leave/unexcused-absences`,
        { employee_id: employeeId, absence_date: absenceDate, notes: notes.trim() }, { headers });
      setEmployeeId(''); setAbsenceDate(''); setNotes(''); setSaved(true); load();
    } catch (e) {
      setError(errorText(e, isRTL ? 'تعذّر إرسال البلاغ' : 'The report could not be sent'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="unexcused-absence-form">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><UserX className="h-4 w-4" />{isRTL ? 'الإبلاغ عن غياب' : 'Report an absence'}</CardTitle>
          <CardDescription>
            {isRTL ? 'للغياب دون عذر مقبول أو إبلاغ مسبق. يُحفظ في ملف الموظف لدى الموارد البشرية ولا يظهر للموظف.'
              : 'For an absence with no valid excuse or prior notice. It is saved to the employee\'s HR file for HR to follow up, and is not shown to the employee.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {members.length === 0 ? (
            <p className="text-sm text-slate-600" data-testid="unexcused-absence-no-team">
              {isRTL ? 'لا يوجد موظفون مرتبطون بك كمدير مباشر.' : 'No employees report to you as their direct manager.'}
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>{isRTL ? 'اسم الموظف' : 'Employee'} *</Label>
                  <Select value={employeeId} onValueChange={setEmployeeId}>
                    <SelectTrigger data-testid="unexcused-absence-employee">
                      <SelectValue placeholder={isRTL ? 'اختر الموظف' : 'Choose the employee'} />
                    </SelectTrigger>
                    <SelectContent>
                      {members.map((m) => <SelectItem key={m.id} value={m.id}>{(isRTL && m.name_ar) || m.name || m.name_ar}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="unexcused-absence-date">{isRTL ? 'تاريخ الغياب' : 'Absence date'} *</Label>
                  <Input id="unexcused-absence-date" type="date" max={todaySaudi()} value={absenceDate}
                    onChange={(e) => setAbsenceDate(e.target.value)} data-testid="unexcused-absence-date" />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="unexcused-absence-notes">
                  {isRTL ? `الملاحظات والتوضيحات (${MIN_NOTE} أحرف على الأقل) *` : `Notes and explanation (at least ${MIN_NOTE} characters) *`}
                </Label>
                <Textarea id="unexcused-absence-notes" rows={3} maxLength={2000} value={notes}
                  onChange={(e) => setNotes(e.target.value)} data-testid="unexcused-absence-notes" />
              </div>
              {error && <p role="alert" className="text-sm text-red-700" data-testid="unexcused-absence-error">{error}</p>}
              {saved && <p role="status" className="text-sm text-green-700">{isRTL ? 'تم إرسال البلاغ إلى الموارد البشرية.' : 'The report was sent to HR.'}</p>}
              <Button disabled={busy || !employeeId || !absenceDate || notes.trim().length < MIN_NOTE} onClick={submit}
                data-testid="unexcused-absence-submit">
                {isRTL ? 'إرسال البلاغ' : 'Submit report'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card data-testid="unexcused-absence-mine">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{isRTL ? 'بلاغاتي' : 'My reports'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-slate-600">{isRTL ? 'لا توجد بلاغات بعد.' : 'No reports yet.'}</p>
          ) : items.map((x) => (
            <div key={x.id} className="flex flex-wrap items-center justify-between gap-2 border-b last:border-0 pb-2 text-sm">
              <span><span className="font-medium">{nameOf(x, isRTL)}</span> · {day(x.absence_date)}</span>
              <span className="flex items-center gap-2">
                <StatusBadge status={x.status} isRTL={isRTL} />
                {x.decision_note && <span className="text-xs text-slate-500">{x.decision_note}</span>}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

/** HR: `employeeId` lists one employee's reports (their file); without it, the pending queue. */
export const UnexcusedAbsenceList = ({ isRTL, employeeId }) => {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [decide, setDecide] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    const params = new URLSearchParams(employeeId ? { employee_id: employeeId } : { status: 'pending' });
    axios.get(`${API_URL}/leave/unexcused-absences?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [token, employeeId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      await axios.post(`${API_URL}/leave/unexcused-absences/${decide.item.id}/decision`,
        { action: decide.action, note: note.trim() || null }, { headers: { Authorization: `Bearer ${token}` } });
      setDecide(null); load();
    } catch (e) {
      setError(errorText(e, isRTL ? 'تعذّر حفظ القرار' : 'The decision could not be saved'));
    } finally {
      setBusy(false);
    }
  };

  if (!employeeId && !items.length) return null;
  const dismissing = decide?.action === 'dismiss';
  return (
    <Card data-testid={employeeId ? 'employee-unexcused-absences' : 'unexcused-absences-panel'}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <UserX className="h-4 w-4" />
          {employeeId ? (isRTL ? 'بلاغات الغياب بدون عذر' : 'Unexcused absence reports')
            : (isRTL ? 'بلاغات غياب بانتظار المراجعة' : 'Unexcused absence reports awaiting review')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-600">{isRTL ? 'لا توجد بلاغات.' : 'No reports.'}</p>}
        {items.map((x) => (
          <div key={x.id} className="flex flex-wrap items-start justify-between gap-2 border-b last:border-0 pb-2 text-sm" data-testid={`unexcused-absence-${x.id}`}>
            <div className="min-w-0 space-y-0.5">
              <p className="font-medium">
                {!employeeId && <>{nameOf(x, isRTL)} · </>}{day(x.absence_date)}
              </p>
              <p className="text-slate-600 whitespace-pre-line">{x.notes}</p>
              <p className="text-xs text-slate-500">
                {isRTL ? 'أبلغ عنه: ' : 'Reported by: '}{nameOf(x, isRTL, 'reported_by')} · {day(x.reported_at)}
              </p>
              {(x.excuse_requests || []).length > 0 && (
                <p className="text-xs text-blue-700" data-testid={`unexcused-absence-excuse-${x.id}`}>
                  {isRTL ? 'قدّم الموظف عذر غياب لنفس التاريخ' : 'The employee filed an absence excuse for this date'}
                  {' '}({x.excuse_requests.map((q) => q.status).join(', ')})
                </p>
              )}
              {x.decision_note && <p className="text-xs text-slate-500">{x.decision_note}</p>}
            </div>
            <div className="flex items-center gap-1">
              <StatusBadge status={x.status} isRTL={isRTL} />
              {x.status === 'pending' && (
                <>
                  <Button size="sm" variant="ghost" className="text-red-700" onClick={() => { setDecide({ item: x, action: 'confirm' }); setNote(''); setError(''); }}>
                    <CheckCircle className="h-4 w-4 me-1" />{isRTL ? 'تأكيد' : 'Confirm'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => { setDecide({ item: x, action: 'dismiss' }); setNote(''); setError(''); }}>
                    <XCircle className="h-4 w-4 me-1" />{isRTL ? 'استبعاد' : 'Dismiss'}
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </CardContent>
      <Dialog open={!!decide} onOpenChange={(v) => !busy && !v && setDecide(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{dismissing ? (isRTL ? 'استبعاد البلاغ' : 'Dismiss the report') : (isRTL ? 'تأكيد الغياب بدون عذر' : 'Confirm the unexcused absence')}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'لا يترتب على القرار أي أثر تلقائي على الرواتب أو الأرصدة أو الإنذارات.'
                : 'The decision has no automatic effect on payroll, balances or warnings.'}
            </DialogDescription>
          </DialogHeader>
          <Label htmlFor="unexcused-absence-decision-note">
            {dismissing ? (isRTL ? `السبب (${MIN_NOTE} أحرف على الأقل) *` : `Reason (at least ${MIN_NOTE} characters) *`) : (isRTL ? 'ملاحظة (اختياري)' : 'Note (optional)')}
          </Label>
          <Textarea id="unexcused-absence-decision-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
          {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setDecide(null)}>{isRTL ? 'إلغاء' : 'Cancel'}</Button>
            <Button variant={dismissing ? 'default' : 'destructive'} disabled={busy || (dismissing && note.trim().length < MIN_NOTE)} onClick={submit}>
              {dismissing ? (isRTL ? 'استبعاد' : 'Dismiss') : (isRTL ? 'تأكيد' : 'Confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
